<template>
    <div>
        <TPAutocomplete
            v-if="initialized"
            :value="value"
            :getter="companyContactGetter"
            :items="items"
            :filter="() => true"
            :defaults="defaults"
            :itemSlot="true"
            label="Company Contact"
            placeholder="Start typing to Search"
            prepend-icon="person"
            item-value="id"
            :display-fields="['name', 'email', 'company_name']"
            :rules="notRequired?[]:[validations.required()]"
            :menu-props="contactAutocompleteMenuProps"
            :clearable="clearable"
            :disabled="disabled"
            :error="error"
            @click="contactAutocompleteMenuProps.value = true"
            @loaded="contactAutocompleteMenuProps.value = true"
            @blur="handleBlurAutocompleteMenu"
            @input="handleInput"
            @change="$emit('change', $event)"
        >
            <template v-slot:item="{ on, attrs, item }">
                <v-divider v-if="item.divider"></v-divider>
                <v-list-item
                    v-else-if="!item.id"
                    v-bind="attrs"
                    class="w-100"
                    @click.stop.prevent="openAddContactModal"
                >
                    <v-list-item-icon>
                        <v-icon>mdi-account-plus</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Add New Contact</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item v-else-if="item.id === -1" v-bind="attrs" v-on="on">
                    <span>{{ item.text }}</span>
                </v-list-item>
                <v-list-item
                    v-else
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="selectContact(item)"
                >
                    <span>{{ [item.name, item.email, item.company_name].join(', ') }}</span>
                </v-list-item>
            </template>
        </TPAutocomplete>
        <v-dialog v-model="showModal" max-width="700" persistent>
            <v-card :loading="isProcessing" :disabled="isProcessing">
                <template v-slot:progress>
                    <v-progress-linear
                        absolute
                        color="success"
                        height="4"
                        indeterminate
                    ></v-progress-linear>
                </template>
                <v-card-title>Add New Contact</v-card-title>
                <v-card-text>
                    <v-form
                        ref="requestData"
                        v-model="validRequestData"
                        @submit.prevent="storeContact"
                    >
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="requestData.name"
                                    label="Name*"
                                    required
                                    :rules="[validations.required()]"
                                    :error-messages="validationErrors.name || []"
                                    @input="clearValidationError('name')"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="requestData.email"
                                    label="Email*"
                                    required
                                    :rules="[validations.required(), validations.email()]"
                                    :error-messages="validationErrors.email || []"
                                    @input="clearValidationError('email')"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="requestData.phone"
                                    label="Phone"
                                    :error-messages="validationErrors.phone || []"
                                    @input="clearValidationError('phone')"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    v-model="requestData.company_id"
                                    :items="companies"
                                    item-text="label"
                                    item-value="id"
                                    label="Company*"
                                    required
                                    :rules="[validations.required()]"
                                    :error-messages="validationErrors.company_id || []"
                                    @input="clearValidationError('company_id')"
                                ></v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="4">
                                <v-checkbox
                                    v-model="requestData.active"
                                    class="mt-0"
                                    label="Active"
                                    hide-details
                                    color="primary"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="warning" @click="closeAddContactModal">Cancel</v-btn>
                    <v-btn color="success" @click="storeContact">Create</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
    SEARCH_COMPANY_CONTACTS,
    CREATE_COMPANY_CONTACT
} from "@/store/companyContact.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import validations from "@/views/partials/form/Validations.vue";
import TPAutocomplete from "@/views/partials/form/Autocomplete.vue";
import { SHOW_SNACK } from "@/store/snack.module";

const getDefaultRequestData = () => ({
    company_id: null,
    name: "",
    email: "",
    phone: "",
    active: true
});

export default {
    components: {
        TPAutocomplete
    },
    props: {
        value: Number,
        notRequired:{
            type: Boolean,
            default: false
        },
        showPanel: {
            type: Boolean,
            default: true
        },
        showAddNew: {
            type: Boolean,
            default: true
        },
        clearable: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        defaults:Object
    },
    data() {
        return {
            initialized: false,
            validations,
            items: [],
            companyContactGetter: SEARCH_COMPANY_CONTACTS,
            contactAutocompleteMenuProps: {
                value: false
            },
            showModal: false,
            isProcessing: false,
            validRequestData: false,
            validationErrors: {},
            companies: [],
            requestData: getDefaultRequestData()
        };
    },
    computed: {
        ...mapGetters(["companyContacts"])
    },
    watch: {
        companyContacts() {
            this.prepareItems();
        },
        defaults() {
            this.prepareItems();
        }
    },
    mounted() {
        if (this.value) {
          this.$store.dispatch(SEARCH_COMPANY_CONTACTS, this.value);
        } else {
          this.prepareItems();
        }
    },
    methods: {
        prepareItems() {
            this.items = [...this.companyContacts];

            if (!this.items.length && !this.defaults) {
                this.items.push({
                    id: -1,
                    text: 'no data available',
                    disabled: true
                });
            }

            this.items.push({
                divider: true
            });

            if (this.showAddNew) {
              this.items.push({
                  name: 'Add New Contact'
              });
            }

            this.initialized = true;
        },
        prependCreatedContact(contact) {
            if (!contact || !contact.id) {
                return;
            }

            const exists = this.items.some(item => item.id === contact.id);
            if (exists) {
                return;
            }

            this.items = this.items.filter(item => !item.divider && item.id !== -1);
            this.items.unshift(contact);

            if (!this.items.length) {
                this.items.push({
                    id: -1,
                    text: "no data available",
                    disabled: true
                });
            }

            this.items.push({
                divider: true
            });

            if (this.showAddNew) {
                this.items.push({
                    name: "Add New Contact"
                });
            }
        },
        handleInput(value) {
            if (value === undefined || value === null || value === -1) {
                return;
            }

            this.$emit("input", value);
            this.handleBlurAutocompleteMenu();
        },
        handleNewContact(id) {
            this.handleInput(id);
        },
        selectContact(item) {
            if (!item || !item.id) {
                return;
            }

            this.$emit("input", item.id);
            this.handleBlurAutocompleteMenu();
        },
        openAddContactModal() {
            this.contactAutocompleteMenuProps.value = false;
            this.validationErrors = {};
            this.requestData = getDefaultRequestData();
            this.showModal = true;
            this.loadCompanies();
        },
        closeAddContactModal() {
            this.showModal = false;
        },
        getErrorMessage(error) {
            if (error && error.errors && typeof error.errors === "object") {
                const firstErrorField = Object.keys(error.errors)[0];
                const firstErrorList = error.errors[firstErrorField];

                if (Array.isArray(firstErrorList) && firstErrorList.length) {
                    return firstErrorList[0];
                }
            }

            if (error && error.message) {
                return error.message;
            }

            return this.$t("GENERAL.UNEXPECTED_ERROR");
        },
        clearValidationError(field) {
            if (!this.validationErrors[field]) {
                return;
            }

            this.$delete(this.validationErrors, field);
        },
        loadCompanies() {
            return this.$store
                .dispatch(GET_COMPANIES_OPTIONS, {})
                .then(response => {
                    this.companies = response.data || [];
                })
                .catch(error => {
                    this.$store.dispatch(SHOW_SNACK, {
                        message: this.getErrorMessage(error),
                        type: "error"
                    });
                });
        },
        storeContact() {
            this.$refs.requestData.validate();
            if (!this.validRequestData) {
                return;
            }

            this.validationErrors = {};
            this.isProcessing = true;
            this.$emit("processing");

            this.$store
                .dispatch(CREATE_COMPANY_CONTACT, this.requestData)
                .then(contact => {
                    this.$emit("new-contact", contact.id);
                    this.$store.dispatch(SHOW_SNACK, {
                        message: `Contact ${contact.name} was added.`,
                        type: "success"
                    });
                    this.closeAddContactModal();
                    this.prependCreatedContact(contact);
                    this.handleNewContact(contact.id);
                })
                .catch(error => {
                    this.validationErrors = (error && error.errors) || {};
                    this.$store.dispatch(SHOW_SNACK, {
                        message: this.getErrorMessage(error),
                        type: "error"
                    });
                })
                .finally(() => {
                    this.isProcessing = false;
                    this.$emit("processed");
                });
        },
        handleBlurAutocompleteMenu() {
            setTimeout(() => {
                this.contactAutocompleteMenuProps.value = false;
            }, 100);
        },
        handleProcessing() {
            this.$emit("processing");
        },
        handleProcessed() {
            this.$emit("processed");
        }
    }
};
</script>
